import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api, { getActiveDistributorId } from "../services/api";

const emptyForm = { name: "", phone: "", email: "", password: "", distributorId: "", roleId: "", active: true, otpEnabled: false, oldestInvoiceClear: false, salesmanCode: "", beatIds: [] };

// the logged-in admin's distributor; new users are created under it (blank for a super admin)
const currentDistributorId = () => getActiveDistributorId();

const Users = ({ onNavigate }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [beats, setBeats] = useState([]);
  const [beatsLoading, setBeatsLoading] = useState(false);

  const selectedRoleName = roles.find((r) => String(r.id) === String(formData.roleId))?.name;
  const isDeliveryBoy = selectedRoleName === "DELIVERY_BOY";

  const loadBeats = async () => {
    try {
      const data = await api.get("/api/v1/beats", { size: 500 });
      setBeats((data?.content || []).filter((b) => b.active));
    } catch (error) {
      console.error("Failed to load beats", error);
      toast.error(error.message || "Failed to load beats");
    }
  };

  const loadRoles = async () => {
    try {
      const data = await api.get("/api/v1/roles");
      // the super admin role (and super admin users) are visible to super admins only
      const isSuperAdmin = String(sessionStorage.getItem("role") || "").replace("ROLE_", "") === "SUPER_ADMIN";
      setRoles((Array.isArray(data) ? data : []).filter((r) => isSuperAdmin || r.name !== "SUPER_ADMIN"));
    } catch (error) {
      toast.error(error.message || "Failed to load roles");
    }
  };

  const loadDistributors = async () => {
    try {
      const data = await api.get("/api/v1/distributors");
      setDistributors(Array.isArray(data) ? data : []);
    } catch (error) {
      // Non-Super-Admins can't list distributors — the Distributor field just won't offer a picker for them.
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/users", { size: 200, role: roleFilter || undefined });
      setUsers(data?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
    loadDistributors();
    loadBeats();
  }, []);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter]);

  const openCreate = () => {
    setEditingId(null);
    setFormData({ ...emptyForm, distributorId: currentDistributorId() });
    setShowModal(true);
  };

  const openEdit = async (user) => {
    setEditingId(user.id);
    const role = roles.find((r) => r.name === user.role);
    setFormData({
      name: user.name || "",
      phone: user.phone || "",
      email: user.email || "",
      password: "",
      distributorId: user.distributorId ?? "",
      roleId: role?.id ?? "",
      active: user.active ?? true,
      otpEnabled: user.otpEnabled ?? false,
      oldestInvoiceClear: user.oldestInvoiceClear ?? false,
      salesmanCode: user.salesmanCode || "",
      beatIds: [],
    });
    setShowModal(true);
    if (user.role === "DELIVERY_BOY") {
      setBeatsLoading(true);
      try {
        const beatIds = await api.get(`/api/v1/users/${user.id}/beats`);
        setFormData((prev) => ({ ...prev, beatIds: Array.isArray(beatIds) ? beatIds : [] }));
      } catch (error) {
        console.error("Failed to load delivery boy beats", error);
        toast.error(error.message || "Failed to load assigned beats");
      } finally {
        setBeatsLoading(false);
      }
    }
  };

  const toggleBeat = (beatId) =>
    setFormData((prev) => ({
      ...prev,
      beatIds: prev.beatIds.includes(beatId) ? prev.beatIds.filter((id) => id !== beatId) : [...prev.beatIds, beatId],
    }));

  // delivery boys get the selected beats; the backend drops them when the role is changed away from delivery boy
  const saveBeats = async (userId) => {
    if (!isDeliveryBoy) return;
    await api.put(`/api/v1/users/${userId}/beats`, { beatIds: formData.beatIds });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        const payload = {
          name: formData.name || null,
          phone: formData.phone || null,
          email: formData.email || null,
          password: formData.password || null,
          roleId: formData.roleId ? Number(formData.roleId) : null,
          active: formData.active,
          otpEnabled: formData.otpEnabled,
          oldestInvoiceClear: formData.oldestInvoiceClear,
          salesmanCode: formData.salesmanCode.trim(),
        };
        await api.put(`/api/v1/users/${editingId}`, payload);
        await saveBeats(editingId);
        toast.success("User updated");
      } else {
        if (!formData.roleId) {
          toast.error("Please select a role");
          setSaving(false);
          return;
        }
        const payload = {
          name: formData.name,
          phone: formData.phone || null,
          email: formData.email,
          password: formData.password,
          distributorId: formData.distributorId ? Number(formData.distributorId) : null,
          roleId: Number(formData.roleId),
          otpEnabled: formData.otpEnabled,
          oldestInvoiceClear: formData.oldestInvoiceClear,
          salesmanCode: formData.salesmanCode.trim() || null,
        };
        const created = await api.post("/api/v1/users", payload);
        if (created?.id) await saveBeats(created.id);
        toast.success("User created");
      }
      setShowModal(false);
      loadUsers();
    } catch (error) {
      console.error("Failed to save user", error);
      toast.error(error.message || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading users...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">All roles</option>
          {roles.map((r) => (
            <option key={r.id} value={r.name}>
              {r.name}
            </option>
          ))}
        </select>
        <button className="primary" onClick={openCreate}>
          + New User
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Distributor</th>
              <th>Role</th>
              <th>Status</th>
              <th title="Mobile app: payments need the customer's OTP">Payment OTP</th>
              <th title="Mobile app: the oldest invoice must be cleared first">Oldest First</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={10}>No users found.</td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>{user.phone || "-"}</td>
                  <td>{user.distributorId ?? "-"}</td>
                  <td>
                    <span className="badge badge-blue">{user.role}</span>
                  </td>
                  <td>
                    <span className={`badge ${user.active ? "badge-green" : "badge-gray"}`}>
                      {user.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>{user.otpEnabled ? "Yes" : "No"}</td>
                  <td>{user.oldestInvoiceClear ? "Yes" : "No"}</td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => openEdit(user)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit User" : "Create User"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name</label>
                  <input name="name" value={formData.name} onChange={handleChange} required={!editingId} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required={!editingId} disabled={!!editingId} />
                </div>
                <div className="form-group">
                  <label>Phone (login OTP is sent here when enabled)</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} maxLength={30} />
                </div>
                <div className="form-group">
                  <label>Salesman Code</label>
                  <input name="salesmanCode" value={formData.salesmanCode} onChange={handleChange} maxLength={50} />
                </div>
                <div className="form-group">
                  <label>{editingId ? "New Password (leave blank to keep)" : "Password"}</label>
                  <input type="password" name="password" value={formData.password} onChange={handleChange} required={!editingId} minLength={8} />
                </div>
                {!editingId && !currentDistributorId() ? (
                  // only a super admin (no distributor of their own) picks the distributor; admins create users under theirs
                  <div className="form-group">
                    <label>Distributor (blank for super admin)</label>
                    {distributors.length > 0 ? (
                      <select name="distributorId" value={formData.distributorId} onChange={handleChange}>
                        <option value="">No distributor (Super Admin)</option>
                        {distributors.map((d) => (
                          <option key={d.distributorId} value={d.distributorId}>
                            {d.name} (#{d.distributorId})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input name="distributorId" value={formData.distributorId} onChange={handleChange} placeholder="Distributor ID" />
                    )}
                    {onNavigate ? (
                      <button
                        type="button"
                        onClick={() => onNavigate("distributors")}
                        style={{
                          marginTop: "6px",
                          fontSize: "12px",
                          background: "none",
                          border: "none",
                          padding: 0,
                          color: "var(--color-primary, #4f46e5)",
                          textDecoration: "underline",
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                      >
                        Don't see it? Manage distributors
                      </button>
                    ) : null}
                  </div>
                ) : null}
                <div className="form-group">
                  <label>Role</label>
                  <select name="roleId" value={formData.roleId} onChange={handleChange} required={!editingId}>
                    <option value="">Select role</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
                {editingId ? (
                  <div className="form-group">
                    <label>Active</label>
                    <select
                      name="active"
                      value={formData.active ? "true" : "false"}
                      onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.value === "true" }))}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                ) : null}
                <div className="form-group">
                  <label>
                    <input type="checkbox" name="otpEnabled" checked={formData.otpEnabled} onChange={handleChange} /> Payment OTP (mobile app)
                  </label>
                </div>
                <div className="form-group">
                  <label>
                    <input type="checkbox" name="oldestInvoiceClear" checked={formData.oldestInvoiceClear} onChange={handleChange} /> Clear oldest invoice first (mobile app)
                  </label>
                </div>
              </div>
              {isDeliveryBoy ? (
                <div className="form-group" style={{ marginTop: "12px" }}>
                  <label>Beats for this delivery boy ({formData.beatIds.length} selected)</label>
                  {beatsLoading ? (
                    <div>Loading assigned beats...</div>
                  ) : beats.length === 0 ? (
                    <div>No active beats found.</div>
                  ) : (
                    <div style={{ maxHeight: "30vh", overflowY: "auto", border: "1px solid #eee", borderRadius: "8px" }}>
                      {beats.map((beat) => (
                        <label
                          key={beat.id}
                          style={{ display: "flex", alignItems: "center", gap: "10px", padding: "6px 10px", borderBottom: "1px solid #f1f1f1" }}
                        >
                          <input type="checkbox" checked={formData.beatIds.includes(beat.id)} onChange={() => toggleBeat(beat.id)} />
                          <span>
                            {beat.beatName}
                            {beat.beatCode ? ` (${beat.beatCode})` : ""}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Users;
